/**
 * 業務のロジック図の見本（mock）の画面 — v2（マッチ度を業務ルールで表す案）と v3（判断をすべて分岐で
 * 表す案）。実データは読まず、見本の材料（`mock-1955.ts`）を描く。
 *
 * 表現の方法を画面で比べるための試しで、ナビにもリンクにも載せない（Dhik 2026-10-01「UI はそれぞれ
 * ~business/, ~business/v2, ~business/v3 として、ボタンは作らなくていいよ」）。見本を持つのは業務 1955
 * だけで、ほかの業務の番号で開くと「見本が無い」と言って業務詳細へのリンクを出す。
 *
 * 図の枠は業務詳細と同じ共有の枠（`FlowWorkspace`）に載せる。全画面 ・ 拡大縮小は枠が持つ。見本なので
 * 案件の左右比較は持たない。v2 は業務ルールの箱を押すと、業務詳細と同じモーダル（`RuleModal`）で
 * 見本の業務ルール（`mock-1955-rules.ts`）の中身を開く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務 1955 のロジック図の見本（v2 ・ v3）を、種類のタグつきの箱と条件つきの線で描く（節点は Figma の図と同じ数）。
 * @graph-connects buildLogicDiagram [calls] 見本の材料を図の節点と線に組む
 * @graph-connects FlowWorkspace [calls] 図・全画面の枠
 * @graph-connects FlowCanvas [calls] 図 1 枚
 */
import { useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DetailSection } from '../components/spec/DetailSection.js';
import { NodeTypeBadge } from '../components/spec/NodeTypeBadge.js';
import { RuleModal, type RuleListRow } from './business-logic-detail.js';
import { buildLogicDiagram, LOGIC_NODE_TYPES } from './business-logic-diagram/logic-build.js';
import { LOGIC_MOCK_V2_RULES } from './business-logic-diagram/mock-1955-rules.js';
import type { LogicDiagramModel } from './business-logic-diagram/logic-model.js';
import {
  LOGIC_MOCK_BUSINESS_ID,
  LOGIC_MOCK_V2,
  LOGIC_MOCK_V3,
} from './business-logic-diagram/mock-1955.js';
import { FlowCanvas } from './flow-workspace/flow-canvas.js';
import { FlowWorkspace } from './flow-workspace/flow-workspace.js';
import { BusinessDetailLink } from './view-links/business-logic.js';

/**
 * 見本の版。
 * @graph-connects none
 */
export type LogicMockVersion = 'v2' | 'v3';

/**
 * 版ごとの、箱を押したときに開く業務ルールの行。v3 は業務ルールを持たない（判断をすべて分岐で表す案）。
 * 見本なので案件の左右比較は持たず、どの行も「変わっていない」として出す。
 * @graph-connects none
 */
const MOCK_RULE_ROWS: Readonly<Record<LogicMockVersion, readonly RuleListRow[]>> = {
  v2: LOGIC_MOCK_V2_RULES.map((rule) => ({
    ruleId: rule.ruleId,
    shown: rule,
    before: null,
    after: rule,
    state: 'unchanged',
  })),
  v3: [],
};

/** 版ごとの見本の材料。 @graph-connects none */
const MOCKS: Readonly<Record<LogicMockVersion, LogicDiagramModel>> = {
  v2: LOGIC_MOCK_V2,
  v3: LOGIC_MOCK_V3,
};

/**
 * ロジック図の見本の画面。パスの業務の番号を読み、見本を持つ業務なら図を描く。
 *
 * @param props 見本の版
 * @returns 見本の画面
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business パスの業務の番号を読み、業務 1955 なら版（v2 ・ v3）の見本の図を、それ以外なら見本が無いことを出す。
 * @graph-connects buildLogicDiagram [calls] 見本の材料を図の節点と線に組む
 * @graph-connects RuleModal [calls] 押した業務ルールのモーダル
 */
export function BusinessLogicMockPage({ version }: { version: LogicMockVersion }) {
  const { t } = useTranslation();
  const { businessId = '' } = useParams({ strict: false });
  const model = MOCKS[version];
  const rows = MOCK_RULE_ROWS[version];
  const [openRuleId, setOpenRuleId] = useState<number | null>(null);
  const openRow = rows.find((row) => row.ruleId === openRuleId) ?? null;
  const diagram = useMemo(
    () =>
      buildLogicDiagram(
        model,
        {
          input: t('logicDiagram.kind.input'),
          rule: t('logicDiagram.kind.rule'),
          ruleOutput: t('logicDiagram.kind.ruleOutput'),
          branch: t('logicDiagram.kind.branch'),
          effect: t('logicDiagram.kind.effect'),
          end: t('logicDiagram.kind.end'),
        },
        /* 業務ルールの中身を持つ版だけ、箱を押せるようにする（業務詳細と同じモーダルを開く）。 */
        rows.length === 0 ? {} : { onSelectRule: setOpenRuleId },
      ),
    [model, rows, t],
  );

  if (businessId !== String(LOGIC_MOCK_BUSINESS_ID)) {
    return (
      <DetailSection title={t(`logicDiagram.${version}.title`)}>
        <p className="text-sm text-[var(--color-text-secondary)]" data-logic-mock-absent>
          {t('logicDiagram.mockAbsent', { id: LOGIC_MOCK_BUSINESS_ID })}
        </p>
        <BusinessDetailLink businessId={LOGIC_MOCK_BUSINESS_ID} pjNumber={undefined}>
          {t('logicDiagram.mockLink')}
        </BusinessDetailLink>
      </DetailSection>
    );
  }

  return (
    <div className="flex min-h-0 flex-col gap-4" data-logic-mock={version}>
      <header className="flex flex-wrap items-center gap-2">
        <NodeTypeBadge nodeType="Business" />
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('logicDiagram.mockBusinessName')}
        </h1>
      </header>
      <p className="text-sm text-[var(--color-text-secondary)]" data-logic-mock-note>
        {t(`logicDiagram.${version}.note`)}
      </p>
      <FlowWorkspace
        layoutHeight={diagram.layout.height}
        canvasWidth={diagram.layout.width}
        panes={({ height }) => (
          <FlowCanvas
            side="mock"
            title={t(`logicDiagram.${version}.title`)}
            height={height}
            nodes={diagram.nodes}
            edges={diagram.edges}
            nodeTypes={LOGIC_NODE_TYPES}
            viewportControl={{ kind: 'own', initial: { x: 0, y: 0, zoom: 1 } }}
          />
        )}
      />
      {openRow !== null && (
        <RuleModal
          row={openRow}
          pjNumber={undefined}
          split={false}
          onToggleCompare={() => undefined}
          onClose={() => {
            setOpenRuleId(null);
          }}
          listedRuleIds={new Set(rows.map((row) => row.ruleId))}
          onOpenRule={setOpenRuleId}
        />
      )}
    </div>
  );
}

/**
 * 見本 v2（マッチ度を業務ルールで表す案）の画面。ルートの `component` に渡す。
 *
 * @returns 見本 v2 の画面
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の見本 v2（マッチ度を業務ルールで表す案）を描く。
 * @graph-connects BusinessLogicMockPage [calls] 見本の画面
 */
export function BusinessLogicMockV2Page() {
  return <BusinessLogicMockPage version="v2" />;
}

/**
 * 見本 v3（判断をすべて分岐で表す案）の画面。ルートの `component` に渡す。
 *
 * @returns 見本 v3 の画面
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の見本 v3（判断をすべて分岐で表す案）を描く。
 * @graph-connects BusinessLogicMockPage [calls] 見本の画面
 */
export function BusinessLogicMockV3Page() {
  return <BusinessLogicMockPage version="v3" />;
}
